// statusUtils.ts
import { Quotation } from './Quotation';

// Interface for status customization from localStorage
export interface QuotationStatus {
  color: string;
  name: string;
  value: string;
  isDefault: boolean;
}

// Safely get custom status colors from localStorage
const getCustomStatusColors = (): QuotationStatus[] | null => {
  try {
    const userPrefs = localStorage.getItem("user_preferences");
    if (!userPrefs) return null;
    
    const colors = JSON.parse(userPrefs)?.quotationCustomization?.quotationStatus;
    if (!Array.isArray(colors)) return null;
    
    return colors;
  } catch (error) {
    console.error('Error loading custom colors:', error);
    return null;
  }
};

// Get status color - always attempt to read from localStorage
export const getStatusColor = (status: string): string => {
  if (!status) return '#8c8c8c'; // Default gray for empty status

  // Get custom colors
  const customColors = getCustomStatusColors();
  
  // Find matching custom color
  if (customColors) {
    const customColor = customColors.find(
      (s) => s.value.toLowerCase() === status.toLowerCase()
    )?.color;
    
    if (customColor) return customColor;
  }
  
  // If no custom color found, generate a consistent color based on status string
  return generateColorFromString(status);
};

// Generate a consistent color based on string input
// This ensures the same status always gets the same color even without localStorage settings
const generateColorFromString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  // Generate a color in HSL space for better visual distinction
  // Using fixed saturation and lightness values for consistency
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 70%, 45%)`;
};

// Check if a status is valid according to the Quotation type
export const isValidStatus = (status: string | undefined): status is Quotation['status'] => {
  if (!status) return false;
  
  // Get the valid statuses from the Quotation type
  const validStatuses = ['pending', 'overdue', 'created', 'approved', 'partially_paid', 'fully_paid', 'ff'];
  return validStatuses.includes(status);
};

// Get high-contrast text color for a background
export const getContrastTextColor = (hexColor: string): string => {
  try {
    // Handle HSL colors
    if (hexColor.startsWith('hsl')) {
      // Parse HSL values
      const hslMatch = /hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/.exec(hexColor);
      if (hslMatch) {
        // Extract lightness
        const lightness = parseInt(hslMatch[3], 10);
        // Simple threshold for HSL
        return lightness > 50 ? '#000000' : '#ffffff';
      }
    }
    
    // Handle hex colors
    // Remove hash if it exists
    let colorHex = hexColor.replace('#', '');
    
    // If shorthand hex (e.g. #abc), convert to full form (e.g. #aabbcc)
    if (colorHex.length === 3) {
      colorHex = colorHex.split('').map(c => c + c).join('');
    }
    
    // Default to dark if invalid hex
    if (colorHex.length !== 6) {
      return '#000000';
    }
  
    // Convert hex to RGB and calculate luminance
    const r = parseInt(colorHex.substr(0, 2), 16) / 255;
    const g = parseInt(colorHex.substr(2, 2), 16) / 255;
    const b = parseInt(colorHex.substr(4, 2), 16) / 255;
    
    // Calculate relative luminance using sRGB formula
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    
    // Use WCAG recommended contrast threshold
    return luminance > 0.5 ? '#000000' : '#ffffff';
  } catch (error) {
    console.error('Error calculating contrast color:', error);
    return '#000000'; // Safe fallback to dark text
  }
};

// Get display name for a status
export const getStatusDisplayName = (status: string): string => {
  if (!status) return '';
  
  // First check if we have a custom name in localStorage
  const customColors = getCustomStatusColors();
  if (customColors) {
    const customStatus = customColors.find(
      (s) => s.value.toLowerCase() === status.toLowerCase()
    );
    
    if (customStatus?.name) return customStatus.name;
  }
  
  // Otherwise, format the status string
  return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
};